import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useOutletContext } from "react-router-dom";

import { type IncidentReport, type IncidentReportRiskAssessmentCore } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from "../../../hooks/useThrottledSectionUpdate";
import { confirm } from "components/Shared/ConfirmDialog";
import { EMPTY_PARTICIPANT } from "../RiskAssessments.constants";

// List-level concerns for the standalone (non-AU) RiskAssessments section: the
// wereThereAnyAtRiskIndividuals gate, the participant count, and add/remove. Per-record
// field editing (risk factors, duty to report, notified parties) lives in the shared
// useRiskAssessmentFieldActions hook instead, so it can also be used by the AU-embedded block.
export function useRiskAssessmentActions() {
  const { t } = useTranslation("criticalIncident", { useSuspense: false });
  const { values, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const throttledUpdateReport = useThrottledSectionUpdate("riskAssessment", values.riskAssessment);
  const participants = values.riskAssessment?.participants ?? [];

  const handleAtRiskChange = async (wereThereAnyAtRiskIndividuals: boolean) => {
    if (!wereThereAnyAtRiskIndividuals && values.riskAssessment?.wereThereAnyAtRiskIndividuals) {
      const confirmed = await confirm({
        title: t("clear_participants_confirm_title"),
        message: t("clear_participants_confirm_text"),
        confirmLabel: t("yes_remove"),
      });
      if (!confirmed) return;
    }

    const nextParticipants = wereThereAnyAtRiskIndividuals
      ? (participants.length ? participants : [{ ...EMPTY_PARTICIPANT }])
      : [];
    const noOfParticipants = wereThereAnyAtRiskIndividuals ? (values.riskAssessment?.noOfParticipants || 1) : 0;

    setFieldValue("riskAssessment.wereThereAnyAtRiskIndividuals", wereThereAnyAtRiskIndividuals);
    setFieldValue("riskAssessment.noOfParticipants", noOfParticipants);
    setFieldValue("riskAssessment.participants", nextParticipants);
    throttledUpdateReport({ wereThereAnyAtRiskIndividuals, noOfParticipants, participants: nextParticipants });
  };

  const handleNumberOfParticipantsChange = (noOfParticipants: number) => {
    const participantsToAdd = Math.max(0, noOfParticipants - participants.length);
    const nextParticipants = [...participants, ...Array.from({ length: participantsToAdd }, () => ({ ...EMPTY_PARTICIPANT }))];

    setFieldValue("riskAssessment.noOfParticipants", noOfParticipants);
    setFieldValue("riskAssessment.participants", nextParticipants);
    throttledUpdateReport({ noOfParticipants, participants: nextParticipants });
  };

  const handleRemoveParticipant = (index: number) => {
    const nextParticipants = participants.filter((_, i) => i !== index);
    const noOfParticipants = nextParticipants.length;

    setFieldValue("riskAssessment.participants", nextParticipants);
    setFieldValue("riskAssessment.noOfParticipants", noOfParticipants);
    throttledUpdateReport({ participants: nextParticipants, noOfParticipants });
  };

  // Passed to useRiskAssessmentFieldActions as `patchRecord` for a given participant index.
  const patchParticipant = (index: number, partial: Partial<IncidentReportRiskAssessmentCore>) => {
    const nextParticipants = participants.map((participant, i) => (i === index ? { ...participant, ...partial } : participant));
    throttledUpdateReport({ participants: nextParticipants });
  };

  return {
    isReadonly,
    participants,
    wereThereAnyAtRiskIndividuals: values.riskAssessment?.wereThereAnyAtRiskIndividuals,
    noOfParticipants: values.riskAssessment?.noOfParticipants,
    riskAssessmentTouched: touched.riskAssessment,
    riskAssessmentErrors: errors.riskAssessment,
    handleAtRiskChange,
    handleNumberOfParticipantsChange,
    handleRemoveParticipant,
    patchParticipant,
  };
}
